/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { Dispatch, SetStateAction } from "react";

import { useTranslation } from "react-i18next";

import Typography from "@mui/material/Typography";

import { Colors, colors } from "@dotnet/telematics-design-system";

import Tooltip from "../../../../components/Tooltip/Tooltip";
import { COMPANY_CARD_STATUS, useFormattedNumber } from "../../../../shared";
import { creditsObjectType } from "../../LeftSide/types";
import { StatusSummaryType } from "../../shared/DeviceStatus/types";
import { useStyles as dashboardStyles } from "../../styles";

interface ChartListItemProps {
  item: StatusSummaryType | creditsObjectType;
  hoveredElement: number | null;
  hovered: boolean;
  backgroundColor: (name: string, colors: Colors) => string;
  borderColor: (name: string, colors: Colors) => string;
  isClickable?: boolean;
  customStyle?: {};
  testId?: string;
  hoveredListItem?: string | null;
  setShowSingleStatusInfo?: Dispatch<SetStateAction<StatusSummaryType | null>>;
  setHoveredListItem?: Dispatch<SetStateAction<string | null>>;
}

const ChartListItem = ({
  item,
  hoveredElement,
  hovered,
  backgroundColor,
  borderColor,
  isClickable,
  customStyle,
  testId,
  hoveredListItem,
  setShowSingleStatusInfo,
  setHoveredListItem
}: ChartListItemProps) => {
  const { formattedNumber } = useFormattedNumber(item.count);

  const { t } = useTranslation();

  const { name } = item;

  const updatedName =
    name === COMPANY_CARD_STATUS.Expired
      ? COMPANY_CARD_STATUS.Deactivated
      : name;

  const { singleChartItemBox, chartItemStick, chartListItemInfoBlock } =
    dashboardStyles({ item, isClickable });

  const itemColorsStyle = {
    backgroundColor: item && colors && backgroundColor(updatedName, colors),
    border: item && colors && `1px solid ${borderColor(updatedName, colors)}`
  };

  const isHoveredStatus =
    hovered || (hoveredElement === null && !hoveredListItem);

  const itemOpacityStyle = {
    opacity: isHoveredStatus ? "1" : "0.3",
    transition: "opacity 0.3s"
  };

  const numberStringIntoNumber = Number.parseFloat(
    formattedNumber.replaceAll(",", "")
  );

  const positiveNumber = numberStringIntoNumber >= 0;
  const formattedNumberColor = positiveNumber ? colors.textDark : colors.red800;

  const handleItemClick = () =>
    setShowSingleStatusInfo?.(item as StatusSummaryType);

  const handleMouseOver = () => {
    setHoveredListItem?.(updatedName);
  };

  const handleMouseLeave = () => {
    setHoveredListItem?.(null);
  };

  const renderContent = () => (
    <div
      css={css([singleChartItemBox, itemOpacityStyle, customStyle])}
      data-testid={testId ?? "doughnut-chart-list-item"}
      onClick={isClickable ? handleItemClick : undefined}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
    >
      {/* colored stick */}
      <div
        css={css([chartItemStick, itemColorsStyle])}
        data-testid={`colored-stick-${backgroundColor(
          updatedName,
          colors
        )}-background`}
      ></div>

      {/* item info */}
      <div css={css(chartListItemInfoBlock)}>
        <Typography
          variant="body2Medium"
          color={formattedNumberColor}
          data-testid={
            positiveNumber ? "positive-item-count" : "negative-item-count"
          }
        >
          {formattedNumber}
        </Typography>
        <Typography variant="body2Medium" color={colors.textPlaceholder}>
          {t(`Dashboard##${updatedName}`)}
        </Typography>
      </div>
    </div>
  );

  return (
    <>
      {isClickable ? (
        <Tooltip
          data-testid={`doughnut-chart-list-item-tooltip-${updatedName}`}
          title={t(`Dashboard##tooltip##click to expand`)}
          placement="top"
          size="medium"
          hasArrow
        >
          {renderContent()}
        </Tooltip>
      ) : (
        renderContent()
      )}
    </>
  );
};

export default ChartListItem;
